/**
 * TopBar — EINZIGE horizontale Kopfleiste der Plattform-Shell.
 *
 * Laut UX_KONZEPT.md §1.2 (konsolidiert) + DESIGN_AUDIT.md Paket PE-1:
 * personal bekommt genau eine dunkle Kopfleiste, die sowohl die
 * Verbund-Elemente (Wortmarke, AppSwitcher, Benutzer, Abmelden) als auch
 * die zuvor im hellen App-Header (Header.tsx) untergebrachten Funktionen
 * (Sidebar-Collapse, globale Suche, EnvBadge, Kontexthilfe) vereint.
 * Header.tsx wurde entfernt — keine zweite Kopfzeile mehr.
 */
import { Menu, LogOut, CircleHelp } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { AppSwitcher } from "@/components/AppSwitcher"
import { EnvBadge } from "@/components/shared"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { dokuAnkerFuerPfad } from "@/docs/routeHilfe"
import { AUTH_MODE, performLogout, useAuthStore } from "@/store/auth"

interface TopBarProps {
  /** Öffnet die bestehende Command-Palette (⌘K / Strg-K) */
  onOpenPalette?: () => void
  /** Klappt die Sidebar ein/aus */
  onToggleSidebar?: () => void
}

export function TopBar({ onOpenPalette, onToggleSidebar }: TopBarProps) {
  const user = useAuthStore((s) => s.user)
  const roles = user?.roles ?? []
  const { pathname } = useLocation()
  const hilfeAnker = dokuAnkerFuerPfad(pathname)

  return (
    <div className="flex h-14 shrink-0 flex-nowrap items-center gap-3 bg-slate-900 px-4 text-white">
      {/* Sidebar-Collapse */}
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label="Sidebar umschalten"
        className="shrink-0 rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Wortmarke — nie gequetscht/abgeschnitten */}
      <a href="/" className="shrink-0 whitespace-nowrap text-sm font-semibold">
        RM<span style={{ color: "#2ecc71" }}>NC</span> Personal
      </a>

      <div className="flex flex-1 items-center justify-end gap-2">
        {/* Such-Trigger — öffnet die bestehende CommandPalette */}
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Suchen oder navigieren"
          className="hidden shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 md:flex"
        >
          <span>Suchen…</span>
          <kbd className="ml-1 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium">
            ⌘K
          </kbd>
        </button>
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Suchen oder navigieren"
          className="shrink-0 rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 md:hidden"
        >
          <SearchIcon />
        </button>

        {/* App-Switcher (9-Punkte-Grid + Panel) */}
        <AppSwitcher currentAppKey="personal" roles={roles} />

        {/* Umgebungs-Badge */}
        <EnvBadge />
        {AUTH_MODE === "dev" && (
          <span className="hidden rounded-full border border-dashed border-amber-400 px-2 py-0.5 text-xs text-amber-300 md:inline">
            Dev-Auth
          </span>
        )}

        {/* Kontexthilfe: Doku-Abschnitt der aktuellen Seite */}
        {hilfeAnker && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                asChild
                aria-label="Hilfe zu dieser Seite"
                className="shrink-0 text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <Link to={`/dokumentation#${hilfeAnker}`}>
                  <CircleHelp className="h-5 w-5" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Hilfe zu dieser Seite</TooltipContent>
          </Tooltip>
        )}

        {/* Benutzername — einziges truncate-Element dieser Zeile */}
        <span className="hidden min-w-0 max-w-[10rem] truncate text-sm text-slate-300 md:inline">
          {user?.name ?? "Nicht angemeldet"}
        </span>

        {/* Abmelden */}
        <button
          type="button"
          onClick={() => void performLogout()}
          aria-label="Abmelden"
          className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden text-sm md:inline">Abmelden</span>
        </button>
      </div>
    </div>
  )
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}
